import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Target,
  Mail,
  Clock,
  ShieldCheck,
  XCircle,
  RefreshCw,
  LogIn,
  UserPlus,
  ArrowLeft,
  HelpCircle,
  Lock,
} from "lucide-react";
import { api } from "@/lib/handball";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function GatePage() {
  const [view, setView] = useState("choice");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };
  useEffect(() => () => stopPolling(), []);

  const startPolling = (e) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/access/status`, { params: { email: e } });
        if (data.status === "approved") {
          stopPolling();
          setView("approved");
        } else if (data.status === "rejected") {
          stopPolling();
          setView("rejected");
        }
      } catch (err) {
        // Poll error – keep polling silently; UI still shows waiting state
        console.debug("[gate] status poll failed", err);
      }
    }, 5000);
  };

  const backToChoice = () => {
    stopPolling();
    setEmail("");
    setPassword("");
    setView("choice");
  };

  // ---------- Login (existing user) ----------
  const submitLogin = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) {
      toast.error("Ange en giltig e-postadress");
      return;
    }
    if (!password) {
      toast.error("Ange lösenord");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email: e, password });
      if (data.status === "ok" && data.token) {
        login(data.token, data.user);
        toast.success("Inloggad");
        navigate("/");
      } else if (data.status === "not_found") {
        setView("notfound");
      } else if (data.status === "pending") {
        setView("pending");
        startPolling(e);
      } else if (data.status === "rejected") {
        setView("rejected");
      } else if (data.status === "disabled") {
        toast.error("Kontot är avstängt. Kontakta admin.");
      } else if (data.status === "bad_password") {
        toast.error("Fel lösenord");
      } else if (data.status === "no_password") {
        toast.error("Kontot saknar lösenord. Kontakta admin.");
      } else {
        toast.error("Inloggning misslyckades");
      }
    } catch {
      toast.error("Kunde inte logga in");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Signup (new user, needs admin approval) ----------
  const submitSignup = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) {
      toast.error("Ange en giltig e-postadress");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/access/request", { email: e });
      toast.success("Ansökan skickad");
      if (data.status === "approved") setView("approved");
      else if (data.status === "rejected") setView("rejected");
      else {
        setView("pending");
        startPolling(e);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kunde inte skicka ansökan");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Forgot password ----------
  const submitForgot = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) {
      toast.error("Ange en giltig e-postadress");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/forgot", { email: e });
      setView("forgot-sent");
    } catch {
      toast.error("Kunde inte skicka länken");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 border-2 border-[#34C759] flex items-center justify-center">
            <Target size={22} className="text-[#34C759]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-black text-2xl uppercase tracking-widest leading-none">
              Målvaktslabb
            </div>
            <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">
              Handboll · Statistik
            </div>
          </div>
        </div>

        <section className="card-tactical p-8 space-y-6" data-testid="gate-card">
          {view === "choice" && <ChoiceView onLogin={() => setView("login")} onSignup={() => setView("signup")} />}

          {view === "login" && (
            <>
              <CredentialForm
                testidPrefix="login"
                title="Logga in"
                subtitle="Ange din e-post och ditt lösenord för att logga in."
                icon={<LogIn size={18} className="mr-2" />}
                submitLabel="Logga in"
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                submitting={submitting}
                onSubmit={submitLogin}
                onBack={backToChoice}
              />
              <button
                type="button"
                data-testid="gate-forgot-link"
                onClick={() => setView("forgot")}
                className="text-xs text-zinc-400 hover:text-white uppercase tracking-widest font-display underline"
              >
                Glömt lösenord?
              </button>
            </>
          )}

          {view === "signup" && (
            <EmailOnlyForm
              testidPrefix="signup"
              title="Aktivera konto"
              subtitle="Skriv in din e-post för att ansöka om åtkomst. När admin godkänt får du ett startlösenord skickat till din mail."
              icon={<UserPlus size={18} className="mr-2" />}
              submitLabel="Skicka ansökan"
              email={email}
              setEmail={setEmail}
              submitting={submitting}
              onSubmit={submitSignup}
              onBack={backToChoice}
            />
          )}

          {view === "forgot" && (
            <EmailOnlyForm
              testidPrefix="forgot"
              title="Glömt lösenord"
              subtitle="Skriv in din e-post så skickar vi en länk som återställer lösenordet till startvärdet."
              icon={<Mail size={18} className="mr-2" />}
              submitLabel="Skicka återställningslänk"
              email={email}
              setEmail={setEmail}
              submitting={submitting}
              onSubmit={submitForgot}
              onBack={() => setView("login")}
            />
          )}

          {view === "forgot-sent" && (
            <StatusView
              testid="gate-forgot-sent"
              icon={<Mail size={32} className="text-[#34C759]" />}
              color="#34C759"
              title="Kolla din mail"
              email={email}
              message={<>Om det finns ett konto kopplat till <strong>{email}</strong> har vi skickat en återställningslänk dit.</>}
              extra={<BackButton onClick={backToChoice} />}
            />
          )}

          {view === "pending" && (
            <StatusView
              testid="gate-pending"
              icon={<Clock size={32} className="text-[#FFD60A]" />}
              color="#FFD60A"
              title="Väntar på godkännande"
              email={email}
              message={<>Din ansökan har skickats för godkännande. När admin godkänt kontot kan du logga in med din e-post och ditt lösenord.</>}
              extra={
                <div className="flex gap-2 justify-center flex-wrap">
                  <Button
                    data-testid="gate-check-status"
                    onClick={async () => {
                      try {
                        const { data } = await api.get(`/access/status`, { params: { email: email.trim().toLowerCase() } });
                        if (data.status === "approved") setView("approved");
                        else if (data.status === "rejected") setView("rejected");
                        else toast.info("Fortfarande under granskning");
                      } catch (err) {
                        console.debug("[gate] manual status check failed", err);
                        toast.error("Kunde inte hämta status");
                      }
                    }}
                    variant="outline"
                    className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white"
                  >
                    <RefreshCw size={14} className="mr-2" />
                    Uppdatera status
                  </Button>
                  <BackButton onClick={backToChoice} />
                </div>
              }
            />
          )}

          {view === "approved" && (
            <StatusView
              testid="gate-approved"
              icon={<ShieldCheck size={32} className="text-[#34C759]" />}
              color="#34C759"
              title="Åtkomst godkänd"
              email={email}
              message={<>Din ansökan är godkänd. Du kan nu logga in med din e-post och ditt lösenord.</>}
              extra={
                <Button
                  data-testid="gate-goto-login"
                  onClick={() => setView("login")}
                  className="rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest"
                >
                  <LogIn size={14} className="mr-2" />
                  Logga in
                </Button>
              }
            />
          )}

          {view === "rejected" && (
            <StatusView
              testid="gate-rejected"
              icon={<XCircle size={32} className="text-[#FF3B30]" />}
              color="#FF3B30"
              title="Åtkomst nekad"
              email={email}
              message={<>Din tidigare ansökan blev nekad. Du kan skicka en ny förfrågan om du vill försöka igen.</>}
              extra={
                <div className="flex gap-2 justify-center flex-wrap">
                  <Button
                    data-testid="gate-request-again"
                    onClick={() => setView("signup")}
                    className="rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest"
                  >
                    Skicka ny förfrågan
                  </Button>
                  <BackButton onClick={backToChoice} />
                </div>
              }
            />
          )}

          {view === "notfound" && (
            <StatusView
              testid="gate-notfound"
              icon={<HelpCircle size={32} className="text-[#FFD60A]" />}
              color="#FFD60A"
              title="Kontot hittades ej"
              email={email}
              message={<>Vi hittade inget konto för <strong>{email}</strong>. Tryck på Aktivera konto för att ansöka om åtkomst.</>}
              extra={
                <div className="flex gap-2 justify-center flex-wrap">
                  <Button
                    data-testid="gate-goto-signup"
                    onClick={() => setView("signup")}
                    className="rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest"
                  >
                    <UserPlus size={14} className="mr-2" />
                    Aktivera konto
                  </Button>
                  <BackButton onClick={backToChoice} />
                </div>
              }
            />
          )}
        </section>

        <div className="mt-6 text-center text-xs text-zinc-600 uppercase tracking-[0.25em] font-display">
          <Link to="/" className="hover:text-zinc-400">
            Målvaktslabb
          </Link>
        </div>
      </div>
    </div>
  );
}

function ChoiceView({ onLogin, onSignup }) {
  return (
    <div className="space-y-5">
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-2">
          Välkommen
        </div>
        <h1 className="font-display font-black uppercase tracking-widest text-3xl text-white leading-tight">
          Målvaktslabb
        </h1>
        <p className="text-zinc-400 mt-3 text-sm leading-relaxed">
          Håll koll på din räddningsstatistik. Välj hur du vill fortsätta.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 pt-2">
        <ChoiceCard
          testid="gate-choice-login"
          icon={<LogIn size={22} className="text-[#34C759]" />}
          title="Logga in"
          subtitle="Jag har redan ett godkänt konto och vill logga in."
          onClick={onLogin}
        />
        <ChoiceCard
          testid="gate-choice-signup"
          icon={<UserPlus size={22} className="text-[#FFD60A]" />}
          title="Aktivera konto"
          subtitle="Jag är ny och vill ansöka om åtkomst till appen."
          onClick={onSignup}
        />
      </div>
    </div>
  );
}

function ChoiceCard({ icon, title, subtitle, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="border border-[#27272A] hover:border-[#34C759] p-4 text-left transition-colors"
    >
      <div className="flex items-center gap-3 mb-1">
        <div className="w-9 h-9 border border-[#3F3F46] flex items-center justify-center bg-[#171717]">
          {icon}
        </div>
        <div className="font-display font-black uppercase tracking-widest text-white">
          {title}
        </div>
      </div>
      <div className="text-xs text-zinc-500 leading-relaxed ml-12">
        {subtitle}
      </div>
    </button>
  );
}

function EmailOnlyForm({ testidPrefix, title, subtitle, icon, submitLabel, email, setEmail, submitting, onSubmit, onBack }) {
  return (
    <>
      <div>
        <button
          type="button"
          data-testid={`${testidPrefix}-back-btn`}
          onClick={onBack}
          className="flex items-center gap-2 text-xs text-zinc-500 hover:text-white uppercase tracking-widest font-display mb-3"
        >
          <ArrowLeft size={12} />
          Tillbaka
        </button>
        <h1 className="font-display font-black uppercase tracking-widest text-3xl text-white leading-tight">
          {title}
        </h1>
        <p className="text-zinc-400 mt-3 text-sm leading-relaxed">{subtitle}</p>
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-400 uppercase tracking-widest text-xs">E-post</Label>
        <Input
          data-testid={`${testidPrefix}-email-input`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="din.mail@example.se"
          autoComplete="email"
          className="bg-black border-[#3F3F46] text-white h-12 focus-visible:ring-[#34C759]"
          onKeyDown={(e) => e.key === "Enter" && onSubmit()}
        />
      </div>
      <Button
        data-testid={`${testidPrefix}-submit-btn`}
        onClick={onSubmit}
        disabled={submitting || !email.trim()}
        className="w-full h-12 rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest text-base disabled:opacity-40"
      >
        {icon}
        {submitLabel}
      </Button>
    </>
  );
}

function CredentialForm({
  testidPrefix,
  title,
  subtitle,
  icon,
  submitLabel,
  email,
  setEmail,
  password,
  setPassword,
  submitting,
  onSubmit,
  onBack,
  passwordHint,
}) {
  return (
    <>
      <div>
        <button
          type="button"
          data-testid={`${testidPrefix}-back-btn`}
          onClick={onBack}
          className="flex items-center gap-2 text-xs text-zinc-500 hover:text-white uppercase tracking-widest font-display mb-3"
        >
          <ArrowLeft size={12} />
          Tillbaka
        </button>
        <h1 className="font-display font-black uppercase tracking-widest text-3xl text-white leading-tight">
          {title}
        </h1>
        <p className="text-zinc-400 mt-3 text-sm leading-relaxed">{subtitle}</p>
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-400 uppercase tracking-widest text-xs">
          E-post
        </Label>
        <Input
          data-testid={`${testidPrefix}-email-input`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="din.mail@example.se"
          autoComplete="email"
          className="bg-black border-[#3F3F46] text-white h-12 focus-visible:ring-[#34C759]"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-400 uppercase tracking-widest text-xs">
          Lösenord
        </Label>
        <Input
          data-testid={`${testidPrefix}-password-input`}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••"
          autoComplete={testidPrefix === "login" ? "current-password" : "new-password"}
          className="bg-black border-[#3F3F46] text-white h-12 focus-visible:ring-[#34C759]"
          onKeyDown={(e) => e.key === "Enter" && onSubmit()}
        />
        {passwordHint && (
          <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
            <Lock size={11} />
            {passwordHint}
          </div>
        )}
      </div>
      <Button
        data-testid={`${testidPrefix}-submit-btn`}
        onClick={onSubmit}
        disabled={submitting || !email.trim() || !password}
        className="w-full h-12 rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest text-base disabled:opacity-40"
      >
        {icon}
        {submitLabel}
      </Button>
    </>
  );
}

function StatusView({ icon, title, message, email, extra, color, testid }) {
  return (
    <div data-testid={testid} className="text-center space-y-4">
      <div
        className="w-16 h-16 mx-auto border-2 flex items-center justify-center"
        style={{ borderColor: color }}
      >
        {icon}
      </div>
      <div>
        <div className="font-display font-black uppercase tracking-widest text-2xl text-white leading-tight">
          {title}
        </div>
        {email && (
          <div className="text-xs text-zinc-500 mt-2 uppercase tracking-widest font-display">
            {email}
          </div>
        )}
      </div>
      <p className="text-zinc-400 text-sm leading-relaxed">{message}</p>
      {extra && <div className="pt-2">{extra}</div>}
    </div>
  );
}

function BackButton({ onClick }) {
  return (
    <Button
      data-testid="gate-back-btn"
      onClick={onClick}
      variant="outline"
      className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white"
    >
      <ArrowLeft size={14} className="mr-2" />
      Tillbaka
    </Button>
  );
}
